import React, { memo } from "react";
import { AlertTriangle, GripVertical, Pin } from "lucide-react";
import { ALLERGY_TAGS, dietLabel, dietShort, initials } from "@/lib/format";
import { t, tf } from "@/i18n";

const GuestChipBase = ({
  guest,
  conflict = false,
  selected = false,
  onSelect,
  onRequestMove,
  onDragStart,
  onDragEnd,
  dragging = false,
  showTable = false,
}) => {
  const noRsvp = !guest.responded;

  return (
    <div
      data-testid={`guest-chip-${guest.id}`}
      data-guest-id={guest.id}
      data-guest-name={guest.name}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData("text/plain", guest.id);
        event.dataTransfer.effectAllowed = "move";
        onDragStart?.(guest);
      }}
      onDragEnd={() => onDragEnd?.(guest)}
      onClick={() => onSelect?.(guest)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect?.(guest);
        }
      }}
      role="button"
      tabIndex={0}
      title={guest.blurb || (noRsvp ? t("chip.notAnswered") : guest.name)}
      className={`group inline-flex max-w-full cursor-grab items-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-1.5 text-left shadow-[0_4px_14px_rgba(31,35,32,0.05)] outline-none hover:border-[color:var(--champagne)] focus-visible:ring-2 focus-visible:ring-[color:var(--sage)] ${
        dragging ? "chip-dragging" : ""
      } ${noRsvp ? "border-dashed" : ""}`}
      style={{
        borderColor: conflict ? "var(--danger)" : selected ? "var(--sage-dark)" : "var(--line)",
        boxShadow: selected ? "0 0 0 2px rgba(126,155,139,0.35)" : undefined,
      }}
    >
      <span
        className="font-num flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold"
        style={{
          backgroundColor: noRsvp ? "#f1efea" : "#f2f6f3",
          color: noRsvp ? "var(--muted-ink)" : "var(--sage-dark)",
        }}
        aria-hidden
      >
        {initials(guest.name)}
      </span>
      <span className="truncate text-[13px] font-medium leading-tight">{guest.name}</span>
      {guest.pinned ? (
        <Pin
          className="h-3 w-3 shrink-0"
          style={{ color: "var(--gild)" }}
          aria-label={t("chip.pinned")}
          data-testid={`guest-pinned-${guest.id}`}
        />
      ) : null}
      {conflict ? (
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--danger)" }} aria-label={t("chip.conflict")} />
      ) : null}
      {noRsvp ? (
        <span
          className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
          style={{ backgroundColor: "#f1efea", color: "var(--muted-ink)" }}
        >
          {t("chip.noRsvp")}
        </span>
      ) : null}
      {(guest.diet_tags || []).slice(0, 2).map((tag) => (
        <span
          key={tag}
          className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
          style={{
            backgroundColor: ALLERGY_TAGS.has(tag) ? "#fdf1f0" : "#f2f6f3",
            color: ALLERGY_TAGS.has(tag) ? "var(--danger)" : "var(--success)",
          }}
          title={dietLabel(tag)}
        >
          {dietShort(tag)}
        </span>
      ))}
      {showTable ? (
        <span className="font-num shrink-0 text-[10px] text-[color:var(--muted-ink)]">
          {guest.table ? `T${guest.table}` : "—"}
        </span>
      ) : null}
      {onRequestMove ? (
        <button
          type="button"
          data-testid={`guest-move-trigger-${guest.id}`}
          aria-label={tf("chip.move", { name: guest.name })}
          onClick={(event) => {
            event.stopPropagation();
            onRequestMove(guest);
          }}
          className="ml-0.5 shrink-0 rounded-full p-1 text-[color:var(--muted-ink)] hover:bg-[color:var(--paper-2)] hover:text-[color:var(--ink)]"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
};

export const GuestChip = memo(GuestChipBase);

export default GuestChip;
